import React from 'react';
import { Tooltip } from '@mui/material';
import {
    FlexDiv,
    SpaceBetweenDiv,
    Heading3,
    ThemedButton,
    TextDefault,
} from './ui';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import DataObjectIcon from '@mui/icons-material/DataObject';

function EditorToolbar({
    question,
    isDirty,
    canSaveInPlace,
    onSave,
    onSaveAs,
    onPreview,
    onShowJson,
    onQuestionSettings,
    onCancel,
    /** When true, in-place Save / Create is always disabled (e.g. super testing). */
    disableSaveInPlace = false,
    /** Tooltip when {@link disableSaveInPlace} is true. */
    saveInPlaceDisabledTooltip = '',
    /** Label for the save-as action (default matches LMS copy). */
    saveAsButtonText,
}) {
    const isNew = !question?.id;
    const showSaveAs = !isNew && !canSaveInPlace;
    const saveAsLabel = saveAsButtonText || 'Save as copy';
    const saveDisabledWhenNotForced =
        !isDirty || (!canSaveInPlace && !isNew);
    const saveDisabled =
        disableSaveInPlace || saveDisabledWhenNotForced;
    const saveAsDisabled = disableSaveInPlace
        ? !isDirty
        : !isDirty || isNew;

    return (
        <div className="editor-toolbar">
            <SpaceBetweenDiv alignCenter padding={16}>
                <FlexDiv alignCenter gap={16}>
                    <Heading3>Question Editor</Heading3>
                    {isDirty && (
                        <TextDefault color="warning">
                            • Unsaved changes
                        </TextDefault>
                    )}
                </FlexDiv>

                <FlexDiv alignCenter gap={12}>
                    {onQuestionSettings ? (
                        <ThemedButton
                            mode="secondary"
                            startIcon={<SettingsIcon />}
                            onClick={onQuestionSettings}
                            text="Question settings"
                        />
                    ) : null}
                    {onPreview ? (
                        <ThemedButton
                            mode="secondary"
                            startIcon={<PreviewIcon />}
                            onClick={onPreview}
                            text="Preview"
                        />
                    ) : null}
                    {onShowJson ? (
                        <ThemedButton
                            mode="secondary"
                            startIcon={<DataObjectIcon />}
                            onClick={onShowJson}
                            text="Show JSON"
                        />
                    ) : null}

                    {disableSaveInPlace && onSaveAs ? (
                        <>
                            <Tooltip
                                title={saveInPlaceDisabledTooltip || ''}
                                arrow
                            >
                                <span>
                                    <ThemedButton
                                        mode="secondary"
                                        startIcon={<SaveIcon />}
                                        disabled
                                        text={
                                            isNew
                                                ? 'Create question'
                                                : 'Save question'
                                        }
                                    />
                                </span>
                            </Tooltip>
                            <ThemedButton
                                mode="primary"
                                startIcon={<FileCopyIcon />}
                                onClick={onSaveAs}
                                disabled={saveAsDisabled}
                                text={saveAsLabel}
                            />
                        </>
                    ) : showSaveAs ? (
                        <ThemedButton
                            mode="primary"
                            startIcon={<FileCopyIcon />}
                            onClick={onSaveAs}
                            disabled={saveAsDisabled}
                            text={saveAsLabel}
                        />
                    ) : (
                        <ThemedButton
                            mode="primary"
                            startIcon={<SaveIcon />}
                            onClick={onSave}
                            disabled={saveDisabled}
                            text={isNew ? 'Create question' : 'Save question'}
                        />
                    )}

                    {!disableSaveInPlace &&
                    !showSaveAs &&
                    !isNew &&
                    onSaveAs ? (
                        <ThemedButton
                            mode="secondary"
                            startIcon={<FileCopyIcon />}
                            onClick={onSaveAs}
                            disabled={saveAsDisabled}
                            text={saveAsLabel}
                        />
                    ) : null}

                    <ThemedButton
                        mode="secondary"
                        startIcon={<CloseIcon />}
                        onClick={onCancel}
                        text="Cancel"
                    />
                </FlexDiv>
            </SpaceBetweenDiv>
        </div>
    );
}

export default EditorToolbar;
